import { useState } from 'react';
import type {
  GetShipmentResponse,
  ShipmentStatus,
  ShipmentType,
} from '@erp/rpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TruckIcon,
  MapPinIcon,
  CalendarIcon,
  PackageIcon,
  ScalesIcon,
  CubeIcon,
  NoteIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import { DateTime, T, Var, Plural, msg, useMessages, useGT } from 'gt-react';

const SHIPMENT_TYPE_LABELS: Record<ShipmentType, string> = {
  incoming: msg('Incoming', {
    $context: 'shipment direction type: goods coming in',
  }) as string,
  outgoing: msg('Outgoing', {
    $context: 'shipment direction type: goods going out',
  }) as string,
};

const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  need_to_be_scheduled: msg('Need To Be Scheduled', {
    $context: 'shipment lifecycle status',
  }) as string,
  scheduled: msg('Scheduled', {
    $context: 'shipment lifecycle status',
  }) as string,
  in_transit: msg('In Transit', {
    $context: 'shipment lifecycle status',
  }) as string,
  delivered: msg('Delivered', {
    $context: 'shipment lifecycle status',
  }) as string,
  cancelled: msg('Cancelled', {
    $context: 'shipment lifecycle status',
  }) as string,
};

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'need_to_be_scheduled':
      return 'secondary';
    case 'scheduled':
      return 'outline';
    case 'in_transit':
      return 'default';
    case 'delivered':
      return 'outline';
    case 'cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

type ShipmentWithDetails = NonNullable<GetShipmentResponse['shipment']>;

interface ShipmentMetadataProps {
  shipment: ShipmentWithDetails;
  chargesCount: number;
}

export function ShipmentMetadata({
  shipment,
  chargesCount,
}: ShipmentMetadataProps) {
  const gt = useGT();
  const m = useMessages();

  const unknownPlaceLabel = gt('Unknown', {
    $context: 'placeholder when shipment origin or destination is not set',
  });

  const emptyDateLabel = gt('—', {
    $context: 'placeholder when a shipment timestamp is not available',
  });

  const typeLabel = m(SHIPMENT_TYPE_LABELS[shipment.type]);
  const statusLabel = m(SHIPMENT_STATUS_LABELS[shipment.status]);

  const displayNumber = shipment.originLoadNumber || shipment.id.slice(0, 8);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TruckIcon className="h-4 w-4" />
            {displayNumber}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {typeLabel}
            </Badge>
            <Badge variant={getStatusVariant(shipment.status)}>
              {statusLabel}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Route Info */}
        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
          <MapPinIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">
              {shipment.origin ?? unknownPlaceLabel}
            </span>
            <span className="text-muted-foreground">
              <T context="arrow between shipment route origin and destination">
                →
              </T>
            </span>
            <span className="font-medium">
              {shipment.destination ?? unknownPlaceLabel}
            </span>
          </div>
        </div>

        {/* Main Shipment Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T context="label for shipment scheduled date/time">Scheduled</T>
            </div>
            <div className="font-medium text-sm">
              {shipment.scheduledAt ? (
                <DateTime
                  options={{
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }}
                >
                  {new Date(shipment.scheduledAt)}
                </DateTime>
              ) : (
                emptyDateLabel
              )}
            </div>
          </div>

          {shipment.departureAt && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                <T context="label for shipment departure time">Departure</T>
              </div>
              <div className="font-medium text-sm">
                <DateTime
                  options={{
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }}
                >
                  {new Date(shipment.departureAt)}
                </DateTime>
              </div>
            </div>
          )}

          {shipment.arrivalAt && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                <T context="label for shipment arrival time">Arrival</T>
              </div>
              <div className="font-medium text-sm">
                <DateTime
                  options={{
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }}
                >
                  {new Date(shipment.arrivalAt)}
                </DateTime>
              </div>
            </div>
          )}

          {shipment.weight && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <ScalesIcon className="h-3 w-3" />
                <T context="label for shipment weight">Weight</T>
              </div>
              <div className="font-medium text-sm">
                <T context="shipment weight with pounds unit">
                  <Var>{shipment.weight.toLocaleString()}</Var> lbs
                </T>
              </div>
            </div>
          )}

          {shipment.volume && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CubeIcon className="h-3 w-3" />
                <T context="label for shipment volume">Volume</T>
              </div>
              <div className="font-medium text-sm">
                <T context="shipment volume with cubic feet unit">
                  <Var>{shipment.volume.toLocaleString()}</Var> cu ft
                </T>
              </div>
            </div>
          )}

          {chargesCount > 0 && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <PackageIcon className="h-3 w-3" />
                <T context="label for freight or accessorial charges count">
                  Charges
                </T>
              </div>
              <div className="font-medium text-sm">
                <T context="shipment: number of charge line items">
                  <Var>{chargesCount}</Var>{' '}
                  <Plural n={chargesCount} one="charge" other="charges" />
                </T>
              </div>
            </div>
          )}
        </div>

        {/* Origin System Info */}
        {shipment.originSystem && (
          <div className="border-t pt-3">
            <div className="text-xs text-muted-foreground">
              <T context="integration system that created the shipment record">
                Origin: <Var>{shipment.originSystem}</Var>
              </T>
            </div>
          </div>
        )}

        {shipment.notes && <NotesSection notes={shipment.notes} />}
      </CardContent>
    </Card>
  );
}

function NotesSection({ notes }: { notes: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const lines = notes
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return null;
  }

  return (
    <div className="border-t pt-3">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground w-full"
      >
        <NoteIcon className="h-3 w-3" />
        <T context="expandable Famous ERP notes section with line count">
          Famous Notes (<Var>{lines.length}</Var>)
        </T>
        <CaretDownIcon
          className={`h-3 w-3 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <ul className="mt-2 space-y-1.5">
          {lines.map((line, index) => (
            <li
              key={`${index}-${line}`}
              className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5 whitespace-pre-wrap"
            >
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
